import type { C1_PointData } from "../types/C1_ModelTypes";
import type { PlacementWall } from "./C1_roomPlacementGeometry";
import { polygonContainsPoint } from "../lib/C1_polygonPlacement";

type Segment = { start: C1_PointData; end: C1_PointData };

/** Independent walls expose two faces without changing the room's usable polygon. */
export function freeWallPlacementFaces(wall: Segment & { id: string; visible: boolean }, thickness: number, polygon: C1_PointData[]): PlacementWall[] {
  const dx = wall.end.x - wall.start.x, dy = wall.end.y - wall.start.y;
  const length = Math.hypot(dx, dy);
  if (length < 0.01) return [];
  const faces: PlacementWall[] = [];
  for (const sign of [1, -1]) {
    const offset = { x: -dy / length * thickness / 2 * sign, y: dx / length * thickness / 2 * sign };
    const start = sign === 1 ? wall.start : wall.end;
    const end = sign === 1 ? wall.end : wall.start;
    const face: PlacementWall = { start: { x: start.x + offset.x, y: start.y + offset.y },
      end: { x: end.x + offset.x, y: end.y + offset.y }, wallId: wall.id, visible: wall.visible,
      side: Math.abs(dx) >= Math.abs(dy) ? dx * sign >= 0 ? "top" : "bottom" : dy * sign >= 0 ? "right" : "left" };
    faces.push(...clipWallFace(face, polygon));
  }
  return faces;
}

/** A long wall may cross multiple rooms; offer only the portions inside this room. */
function clipWallFace(face: PlacementWall, polygon: C1_PointData[]): PlacementWall[] {
  if (polygon.length < 3) return [];
  const dx = face.end.x - face.start.x, dy = face.end.y - face.start.y;
  const offsets = [0, 1];
  for (let index = 0; index < polygon.length; index++) {
    const a = polygon[index], b = polygon[(index + 1) % polygon.length];
    const ex = b.x - a.x, ey = b.y - a.y;
    const cross = dx * ey - dy * ex;
    if (Math.abs(cross) < 1e-10) continue;
    const ax = a.x - face.start.x, ay = a.y - face.start.y;
    const t = (ax * ey - ay * ex) / cross;
    const u = (ax * dy - ay * dx) / cross;
    if (t > 0 && t < 1 && u >= 0 && u <= 1) offsets.push(t);
  }
  offsets.sort((a, b) => a - b);
  const at = (offset: number) => ({ x: face.start.x + dx * offset, y: face.start.y + dy * offset });
  return offsets.slice(0, -1).flatMap((start, index) => {
    const end = offsets[index + 1];
    return end - start > 1e-8 && polygonContainsPoint(polygon, at((start + end) / 2))
      ? [{ ...face, start: at(start), end: at(end) }] : [];
  });
}

/** Keeps docked objects at their wall fraction and at the same distance from the face. */
export function transformFreeWallPoint(point: C1_PointData, before: Segment, after: Segment): C1_PointData {
  const dx = before.end.x - before.start.x, dy = before.end.y - before.start.y;
  const oldLength = Math.hypot(dx, dy), nx = after.end.x - after.start.x, ny = after.end.y - after.start.y;
  const newLength = Math.hypot(nx, ny);
  if (oldLength < 0.01 || newLength < 0.01) return { ...point };
  const x = point.x - before.start.x, y = point.y - before.start.y;
  const along = (x * dx + y * dy) / (oldLength * oldLength);
  const across = (dx * y - dy * x) / oldLength;
  return { x: after.start.x + along * nx - ny / newLength * across,
    y: after.start.y + along * ny + nx / newLength * across };
}
